import type { PrimeVueConfiguration } from 'primevue'
import type { DefineComponent } from 'vue'

import { createInertiaApp, Link } from '@inertiajs/vue3'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import { createPinia } from 'pinia'
import { ConfirmationService } from 'primevue'

import '@/assets/fonts/material-symbols.css'

import PrimeVue from 'primevue/config'
import ConfirmDialog from 'primevue/confirmdialog'
import FocusTrap from 'primevue/focustrap'
import StyleClass from 'primevue/styleclass'
import Toast from 'primevue/toast'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'
import { createApp, h } from 'vue'
import { ZiggyVue } from 'ziggy-js'

import type { CalloutPageProps } from '@/domains/integrations/callout/callout.types'
import type { AbilitiesIdentityProps } from '@/domains/rbac/rbac.types'
import type { TenantTimezoneProps } from '@/utils/date-util/dateUtil.types'

import { installBulkOperationsAdapters } from '@/composables/use-bulk-operations/installBulkOperationsAdapters'
import { installGuardPopstateInterceptor } from '@/composables/use-unsaved-changes-guard/guardPopstateInterceptor'
import { TwinePreset } from '@/domains/design-system/theme.ts'
import { installCallout } from '@/domains/integrations/callout/installCallout'
import { installServerErrorReporter } from '@/domains/notifications/server-error/installServerErrorReporter'
import { createAppRbac } from '@/domains/rbac/rbac'
import AppLayout from '@/layouts/app-layout/AppLayout.vue'
import AuthLayout from '@/layouts/auth-layout/AuthLayout.vue'
import { installQueryPlugin } from '@/support/query/installQueryPlugin'
import { setTenantTimezone } from '@/utils/date-util/dateUtil'

const pinia = createPinia()

// Must run before createInertiaApp so the guard's popstate handler fires before
// Inertia's, allowing stopImmediatePropagation() to block Inertia on sentinel events.
installGuardPopstateInterceptor()

const primeConfig: PrimeVueConfiguration = {
  theme: {
    preset: TwinePreset,
    options: {
      // DEC-2026-060: dark (beta) applies under this class; `useAppearance` sets it on <html>.
      darkModeSelector: '.app-dark',
      cssLayer: false // Optional: set to true if using CSS layers,
    }
  }
}

void createInertiaApp({
  title: title => `${title} - Twine`,
  resolve: async name => {
    const page = await resolvePageComponent(
      `./Pages/${name}.vue`,
      import.meta.glob<DefineComponent>('./Pages/**/*.vue')
    )

    const showBaseLayout = name.startsWith('Auth/') || name === 'Offline'

    page.default.layout = showBaseLayout ? AuthLayout : page.default.layout || AppLayout

    return page
  },
  setup({ el, App, props, plugin }) {
    const app = createApp({ render: () => h(App, props) })

    app
      .use(plugin)
      .use(PrimeVue, primeConfig)
      .use(pinia)
      .use(ZiggyVue)
      .use(ToastService)
      .use(ConfirmationService)
      .component('Link', Link)
      .component('ConfirmDialog', ConfirmDialog)
      .component('Toast', Toast)
      .directive('focustrap', FocusTrap)
      .directive('styleclass', StyleClass)
      .directive('tooltip', Tooltip)

    installQueryPlugin(app)
    installBulkOperationsAdapters(app)
    // After app.use(pinia), so the reporter can reach the toast store when a visit fails.
    installServerErrorReporter()

    // Bootstrap props are read here, not inside the plugin: Inertia only
    // populates the ref behind `usePage()` once the App component mounts, which is
    // after every plugin has installed. These carry the same values already.
    const bootstrapProps = props.initialPage.props as AbilitiesIdentityProps & CalloutPageProps & TenantTimezoneProps

    // They seed the identity; RBAC tracks navigation itself from there, because
    // signing in is a visit rather than a page load and never re-runs this.
    app.use(createAppRbac(bootstrapProps))

    // Reads the same bootstrap props, and stays inert unless the environment sets a
    // project id. Staging only, today.
    installCallout(bootstrapProps)

    // Every "today" in the admin is the tenant's day, whatever the browser's zone. Set before
    // mount, because a page reads it for its defaults as it sets up.
    setTenantTimezone(bootstrapProps.tenantTimezone)

    app.mount(el)
  },
  progress: {
    color: 'var(--p-surface-600)'
  }
})
